import React, { useState } from 'react';
import { CheckCircleIcon, EditIcon } from 'lucide-react';
interface ReviewStepProps {
  formData: {
    name: string;
    displaySequence: number;
    isPopular: boolean;
    type: string;
    description: string;
  };
  selectedProducts: any[];
  selectedSKUs: any[];
  onBack: () => void;
  onSubmit: () => void;
  onSaveDraft: () => void;
}
export const ReviewStep: React.FC<ReviewStepProps> = ({
  formData,
  selectedProducts,
  selectedSKUs,
  onBack,
  onSubmit,
  onSaveDraft
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<'compact' | 'detailed'>('compact');
  const handleSubmit = () => {
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      onSubmit();
    }, 1000);
  };
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'spu':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-344">
            SPU Based
          </span>;
      case 'sku':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-345">
            SKU Specific
          </span>;
      case 'mixed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-346">
            Mixed
          </span>;
      default:
        return null;
    }
  };
  const totalItems = selectedProducts.length + selectedSKUs.length;
  const totalVariants = selectedProducts.reduce((acc, product) => acc + product.variants.length, 0) + selectedSKUs.length;
  return <div className="p-6" data-id="element-347">
      {/* Collection Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-id="element-348">
        <div className="flex justify-between items-start" data-id="element-349">
          <div data-id="element-350">
            <h3 className="text-xl font-medium text-gray-900" data-id="element-351">
              {formData.name}
            </h3>
            <div className="flex items-center mt-2 space-x-3" data-id="element-352">
              {getTypeLabel(formData.type)}
              <span className="text-sm text-gray-500" data-id="element-353">
                Sequence: {formData.displaySequence}
              </span>
              <span className="text-sm text-gray-500" data-id="element-354">
                {formData.isPopular ? 'Popular' : 'Not Popular'}
              </span>
            </div>
            {formData.description && <p className="mt-2 text-gray-600" data-id="element-355">{formData.description}</p>}
          </div>
          <button className="text-gray-400 hover:text-blue-600" data-id="element-356">
            <EditIcon className="h-5 w-5" data-id="element-357" />
          </button>
        </div>
      </div>
      {/* Items Review Section */}
      <div className="mb-6" data-id="element-358">
        <div className="flex justify-between items-center mb-4" data-id="element-359">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-360">
            Items in This Collection ({totalItems})
          </h3>
          <div className="flex border border-gray-200 rounded-md overflow-hidden" data-id="element-361">
            <button className={`px-3 py-1.5 text-sm ${viewMode === 'compact' ? 'bg-gray-100 text-gray-800' : 'bg-white text-gray-600'}`} onClick={() => setViewMode('compact')} data-id="element-362">
              Compact List
            </button>
            <button className={`px-3 py-1.5 text-sm ${viewMode === 'detailed' ? 'bg-gray-100 text-gray-800' : 'bg-white text-gray-600'}`} onClick={() => setViewMode('detailed')} data-id="element-363">
              Detailed Preview
            </button>
          </div>
        </div>
        {totalItems === 0 ? <div className="bg-gray-50 border border-gray-200 border-dashed rounded-lg p-8 text-center" data-id="element-364">
            <p className="text-gray-500" data-id="element-365">
              No items added to this collection yet.
            </p>
            <p className="text-sm text-gray-400 mt-1" data-id="element-366">
              You can still create the collection and add items later.
            </p>
          </div> : viewMode === 'compact' ? <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-367">
            <ul className="divide-y divide-gray-200" data-id="element-368">
              {selectedProducts.map(product => <li key={product.id} className="p-4 flex justify-between items-center" data-id="element-369">
                  <div className="flex items-center" data-id="element-370">
                    <div className="h-10 w-10 bg-blue-50 rounded flex items-center justify-center mr-3" data-id="element-371">
                      <img src={product.image} alt={product.name} className="max-h-8 max-w-8 object-contain" data-id="element-372" />
                    </div>
                    <div data-id="element-373">
                      <h4 className="font-medium text-gray-800" data-id="element-374">
                        {product.name}
                      </h4>
                      <div className="text-sm text-gray-500" data-id="element-375">
                        {product.variants.length} variants
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-blue-600" data-id="element-376">SPU</div>
                </li>)}
              {selectedSKUs.map(sku => <li key={sku.id} className="p-4 flex justify-between items-center" data-id="element-377">
                  <div className="flex items-center" data-id="element-378">
                    <div className="h-10 w-10 bg-green-50 rounded flex items-center justify-center mr-3" data-id="element-379">
                      <img src={sku.image} alt={sku.name} className="max-h-8 max-w-8 object-contain" data-id="element-380" />
                    </div>
                    <div data-id="element-381">
                      <h4 className="font-medium text-gray-800" data-id="element-382">{sku.name}</h4>
                      <div className="text-sm text-gray-500" data-id="element-383">
                        SKU: {sku.sku}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-green-600" data-id="element-384">SKU</div>
                </li>)}
            </ul>
            <div className="bg-gray-50 p-4 border-t border-gray-200" data-id="element-385">
              <div className="flex justify-between items-center" data-id="element-386">
                <span className="text-sm text-gray-600" data-id="element-387">Total Variants:</span>
                <span className="font-medium" data-id="element-388">{totalVariants}</span>
              </div>
            </div>
          </div> : <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-389">
            <p className="text-center text-sm text-gray-500 mb-4" data-id="element-390">
              This is how your collection will look to customers
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-391">
              {selectedProducts.flatMap(product => [<div key={product.id} className="bg-white border border-gray-200 rounded-lg p-3 relative" data-id="element-392">
                  <div className="h-32 flex items-center justify-center mb-2" data-id="element-393">
                    <img src={product.image} alt={product.name} className="max-h-full object-contain" data-id="element-394" />
                  </div>
                  <h5 className="font-medium text-gray-800 mb-1 truncate" data-id="element-395">
                    {product.name}
                  </h5>
                  <div className="text-sm text-blue-600" data-id="element-396">
                    ${product.variants[0].price} - $
                    {Math.max(...product.variants.map(v => v.price))}
                  </div>
                </div>])}
              {selectedSKUs.map(sku => <div key={sku.id} className="bg-white border border-gray-200 rounded-lg p-3 relative" data-id="element-397">
                  <div className="h-32 flex items-center justify-center mb-2" data-id="element-398">
                    <img src={sku.image} alt={sku.name} className="max-h-full object-contain" data-id="element-399" />
                  </div>
                  <h5 className="font-medium text-gray-800 mb-1 truncate" data-id="element-400">
                    {sku.name}
                  </h5>
                  <div className="text-sm text-blue-600" data-id="element-401">${sku.price}</div>
                </div>)}
            </div>
          </div>}
      </div>
      {/* Bottom Actions */}
      <div className="flex justify-between" data-id="element-402">
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-403">
          Back to Add Products
        </button>
        <div data-id="element-404">
          <button onClick={onSaveDraft} className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 mr-3" data-id="element-405">
            Save as Draft
          </button>
          <button onClick={handleSubmit} disabled={isSubmitting} className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center ${isSubmitting ? 'opacity-80 cursor-not-allowed' : ''}`} data-id="element-406">
            {isSubmitting ? <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-407">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-408"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-409"></path>
                </svg>
                Creating...
              </> : 'Create Collection'}
          </button>
        </div>
      </div>
      {/* Success Message (would be shown after successful submission) */}
      {false && <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50" data-id="element-410">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full" data-id="element-411">
            <div className="flex justify-center mb-4" data-id="element-412">
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center text-green-500" data-id="element-413">
                <CheckCircleIcon className="h-8 w-8" data-id="element-414" />
              </div>
            </div>
            <h3 className="text-xl font-medium text-center mb-2" data-id="element-415">
              Collection Created!
            </h3>
            <p className="text-gray-500 text-center mb-6" data-id="element-416">
              Your collection "{formData.name}" has been successfully created.
            </p>
            <div className="flex justify-center space-x-4" data-id="element-417">
              <button className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-418">
                View Collection
              </button>
              <button className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-419">
                Create Another
              </button>
            </div>
          </div>
        </div>}
    </div>;
};